export interface ServiceItem {
  slug: string;
  meta: string;
  title: string;
  tagline: string;
  body: string;
  highlights: string[];
}

export const serviceItemsEn: ServiceItem[] = [
  {
    slug: 'internal-software',
    meta: 'Self-hosted infrastructure',
    title: 'Internal Software & Self-Hosted Infrastructure',
    tagline:
      'Custom internal systems for companies that have outgrown their current tools — on infrastructure you actually own.',
    body:
      'We build the systems your team runs on every day: CRM, ops dashboards, workflow engines, and client portals  deployed on modern self-hosted infrastructure with no vendor lock-in. When off-the-shelf tools stop fitting how you operate, we engineer software around your actual processes.',
    highlights: [
      'Modern stack you control — code, data & deployment',
      'Structured records, permissions & audit-backed workflows',
      'Built for companies that have outgrown spreadsheets and SaaS limits',
    ],
  },
  {
    slug: 'process-automation',
    meta: 'n8n & integrations',
    title: 'Process Automation',
    tagline:
      'Optimize complex workflows, reduce manual tasks, and deploy no-code automations on infrastructure you control.',
    body:
      'From repetitive back-office tasks to full lead pipelines, we design automations that connect your tools and trigger the next step automatically — including n8n workflows for forms, Slack, Google Sheets, CRM sync, and follow-up sequences.',
    highlights: [
      'Complex workflow optimization & manual task reduction',
      'n8n flows: web form → Sheets + Slack + welcome email',
      'Alerts, follow-ups & visual flow documentation',
    ],
  },
  {
    slug: 'chatbots-virtual-agents',
    meta: '24/7 customer engagement',
    title: 'Chatbots & Virtual Agents',
    tagline:
      'Smart virtual assistants and autonomous agents that transform how you engage customers across web, WhatsApp, and internal channels.',
    body:
      'We deploy intelligent chatbots and autonomous agents that handle support, qualify leads, and answer questions around the clock — integrated with your knowledge base and existing business tools.',
    highlights: [
      'Intelligent assistants available around the clock',
      'Autonomous agents for customer support & sales',
      'Integrated with your tools and knowledge base',
    ],
  },
  {
    slug: 'rags-knowledge-systems',
    meta: 'RAG & document AI',
    title: 'RAGs & Knowledge Systems',
    tagline:
      'Retrieval-Augmented Generation so your AI accesses and uses your company knowledge in real time.',
    body:
      'Turn manuals, PDFs, wikis, and internal databases into a live knowledge layer your team and AI agents can query instantly — with accurate, context-aware responses grounded in your data.',
    highlights: [
      'Real-time access to your company knowledge base',
      'Document ingestion: PDFs, wikis, databases & APIs',
      'Accurate, context-aware AI responses for your team',
    ],
  },
  {
    slug: 'custom-dashboards',
    meta: 'Interactive dashboards',
    title: 'Custom Dashboards',
    tagline:
      'Interactive control panels tailored to your KPIs — including automated financial dashboards built from invoice PDFs.',
    body:
      'Visualize the metrics that matter with dashboards designed for how you operate. We also build financial dashboards that read invoice PDFs locally — revenue, expenses, VAT, and client dependency alerts without a SaaS subscription.',
    highlights: [
      'Interactive panels designed for your KPIs',
      'Financial dashboard from PDFs: revenue, expenses & VAT',
      'Monthly charts, quarterly detail & client dependency alerts',
    ],
  },
  {
    slug: 'forecasting-classification',
    meta: 'ML & data models',
    title: 'Forecasting & Classification',
    tagline:
      'Predictive models and intelligent classification for sales, demand, risk, and customer segments.',
    body:
      'Anticipate trends before they hit your P&L with models trained on your data. From demand forecasting to automated classification and segmentation, we apply machine learning where it creates measurable operational advantage.',
    highlights: [
      'Predictive models for trends & demand forecasting',
      'Intelligent data classification & segmentation',
      'Applied to finance, operations & customer analytics',
    ],
  },
  {
    slug: 'websites-virtual-businesses',
    meta: 'Premium sites & brands',
    title: 'Websites & Virtual Businesses',
    tagline:
      'Premium landing pages, Instagram-to-website conversion, branding, and full sales automation — ready to publish in hours.',
    body:
      'End-to-end web presence for local businesses, creators, and service brands: Apple-style scroll animations, Instagram profile sync, packaging design, and automated sales flows — delivered as publish-ready HTML on your hosting.',
    highlights: [
      'Premium sites with scroll effects — ready in hours',
      'Instagram profile → professional website in one session',
      'Branding, packaging design & full sales automation',
    ],
  },
  {
    slug: 'technical-seo-audit',
    meta: 'Technical analysis',
    title: 'Technical SEO Audit',
    tagline:
      'Complete SEO report with score /100, prioritized fixes, and copy-paste ready code corrections.',
    body:
      'We analyze any URL and produce a visual HTML dashboard covering meta tags, headings, images, links, Open Graph, schema, speed, robots.txt, and sitemap — with the top 5 fixes ranked by impact and optional direct application in your codebase.',
    highlights: [
      'Overall score /100 with visual HTML dashboard',
      'Meta tags, headings, images, speed, schema & sitemap',
      'Top 5 fixes with ready-to-copy code',
    ],
  },
  {
    slug: 'meta-ads-audit',
    meta: 'Facebook & Instagram',
    title: 'Meta Ads Audit',
    tagline:
      'Find out why your Meta ads are not converting — ad library review, landing page audit, and message match analysis.',
    body:
      'We audit active campaigns via the Meta Ad Library, evaluate landing page coherence, and rewrite headlines and CTAs with an impact-prioritized checklist so you know exactly what to change first.',
    highlights: [
      'Active ads analysis via Meta Ad Library',
      'Landing page audit & message match (copy / CTA)',
      'Headline & CTA rewrites with impact checklist',
    ],
  },
  {
    slug: 'global-digital-audit',
    meta: '360° diagnostic',
    title: 'Global Digital Audit',
    tagline:
      '360° view of your online business — web, social, offer, funnel, and brand consistency with a prioritized action plan.',
    body:
      'Ideal as an entry-point engagement: we score each domain, map your current funnel, detect inconsistencies, and deliver weekly quick wins plus an optional competitor comparison.',
    highlights: [
      'Score by domain with detected inconsistencies',
      'Current funnel map & prioritized action plan',
      'Weekly quick wins + optional competitor comparison',
    ],
  },
  {
    slug: 'automated-prospecting',
    meta: 'Lead generation',
    title: 'Automated B2B Prospecting',
    tagline:
      'Qualified prospects by niche and city, digital presence analysis, and personalized outreach messages ready to send.',
    body:
      'We build a ranked pipeline of opportunities in your sector, analyze top leads in detail, and generate personalized contact messages with export to JSON or Google Sheets — for your team or agency clients.',
    highlights: [
      'Prospect pipeline ranked by opportunity',
      'Detailed analysis of top leads',
      'Personalized messages + JSON / Google Sheets export',
    ],
  },
  {
    slug: 'custom-ai-tools',
    meta: 'Premium / quoted',
    title: 'Custom AI Tools',
    tagline:
      'Bespoke AI tools for your industry — automate what you do manually every week. CSV to product sheets, LinkedIn analysis, auto quotes, and more.',
    body:
      'We design and install reusable AI tools tailored to your workflow — with shareable kits and setup instructions so your team can run them independently. Premium engagement, scoped to your use case.',
    highlights: [
      'Reusable tool installed for your team',
      'Shareable kit with setup instructions',
      'Automates weekly manual tasks in your industry',
    ],
  },
];

export const serviceItemsEs: ServiceItem[] = [
  {
    slug: 'internal-software',
    meta: 'Infraestructura autohospedada',
    title: 'Software Interno e Infraestructura Autohospedada',
    tagline:
      'Sistemas internos a medida para empresas que han superado sus herramientas actuales — en infraestructura que realmente posees.',
    body:
      'Construimos los sistemas con los que tu equipo opera cada día: CRM, dashboards operativos, motores de workflow y portales de clientes — desplegados en infraestructura moderna autohospedada sin dependencia de proveedores. Cuando el software genérico ya no encaja, diseñamos alrededor de tus procesos reales.',
    highlights: [
      'Stack moderno que controlas — código, datos y despliegue',
      'Registros estructurados, permisos y workflows con auditoría',
      'Para empresas que superaron hojas de cálculo y límites SaaS',
    ],
  },
  {
    slug: 'process-automation',
    meta: 'n8n e integraciones',
    title: 'Automatización de Procesos',
    tagline:
      'Optimiza flujos complejos, reduce tareas manuales y despliega automatizaciones sin código en infraestructura que controlas.',
    body:
      'Desde tareas repetitivas de back-office hasta pipelines completos de leads, diseñamos automatizaciones que conectan tus herramientas y disparan el siguiente paso — incluyendo workflows n8n para formularios, Slack, Google Sheets, CRM y secuencias de seguimiento.',
    highlights: [
      'Optimización de workflows y reducción de tareas manuales',
      'Flujos n8n: formulario web → Sheets + Slack + email de bienvenida',
      'Sincronización, alertas, seguimientos y documentación visual',
    ],
  },
  {
    slug: 'chatbots-virtual-agents',
    meta: 'Atención 24/7',
    title: 'Chatbots y Agentes Virtuales',
    tagline:
      'Asistentes virtuales inteligentes y agentes autónomos que transforman la interacción con tus clientes en web, WhatsApp y canales internos.',
    body:
      'Desplegamos chatbots y agentes autónomos que gestionan soporte, califican leads y responden preguntas las 24 horas — integrados con tu base de conocimientos y herramientas existentes.',
    highlights: [
      'Asistentes inteligentes disponibles las 24 horas',
      'Agentes autónomos para soporte al cliente y ventas',
      'Integrados con tus herramientas y base de conocimientos',
    ],
  },
  {
    slug: 'rags-knowledge-systems',
    meta: 'RAG y documentos IA',
    title: 'RAGs y Sistemas de Conocimiento',
    tagline:
      'Retrieval-Augmented Generation para que tu IA acceda y utilice el conocimiento de tu empresa en tiempo real.',
    body:
      'Convierte manuales, PDFs, wikis y bases de datos internas en una capa de conocimiento consultable al instante — con respuestas precisas y contextualizadas basadas en tus datos.',
    highlights: [
      'Acceso en tiempo real a la base de conocimientos de tu empresa',
      'Ingesta de documentos: PDFs, wikis, bases de datos y APIs',
      'Respuestas IA precisas y contextualizadas para tu equipo',
    ],
  },
  {
    slug: 'custom-dashboards',
    meta: 'Dashboards interactivos',
    title: 'Dashboards Personalizables',
    tagline:
      'Paneles de control interactivos a medida — incluyendo dashboards financieros automáticos desde facturas PDF.',
    body:
      'Visualiza las métricas que importan con dashboards diseñados para cómo operas. También construimos dashboards financieros que leen facturas PDF localmente — ingresos, gastos, IVA y alertas de dependencia de clientes sin suscripción SaaS.',
    highlights: [
      'Paneles interactivos diseñados para tus KPIs',
      'Dashboard financiero desde PDFs: ingresos, gastos e IVA',
      'Gráficos mensuales, detalle trimestral y alertas de dependencia',
    ],
  },
  {
    slug: 'forecasting-classification',
    meta: 'ML y modelos de datos',
    title: 'Pronósticos y Clasificación',
    tagline:
      'Modelos predictivos y clasificación inteligente para ventas, demanda, riesgo y segmentos de clientes.',
    body:
      'Anticipa tendencias antes de que impacten tu P&L con modelos entrenados en tus datos. Desde pronóstico de demanda hasta clasificación y segmentación automática, aplicamos machine learning donde genera ventaja operativa medible.',
    highlights: [
      'Modelos predictivos para tendencias y demanda',
      'Clasificación inteligente y segmentación de datos',
      'Aplicado a finanzas, operaciones y analítica de clientes',
    ],
  },
  {
    slug: 'websites-virtual-businesses',
    meta: 'Sitios premium y marcas',
    title: 'Creación de Páginas Web / Empresas Virtuales',
    tagline:
      'Landing pages premium, conversión Instagram → sitio web, branding y automatización de ventas — listas para publicar en horas.',
    body:
      'Presencia web integral para comercios locales, creadores y marcas de servicios: animaciones al scroll estilo Apple, sincronización de Instagram, diseño de empaque y flujos de venta automatizados — entregados como HTML listo en tu hosting.',
    highlights: [
      'Sitios premium con efectos scroll — listos en horas',
      'Perfil Instagram → sitio web profesional en una sesión',
      'Branding, diseño de empaque y automatización completa de ventas',
    ],
  },
  {
    slug: 'technical-seo-audit',
    meta: 'Análisis técnico',
    title: 'Auditoría SEO Técnica',
    tagline:
      'Informe SEO completo con score /100, correcciones priorizadas y código listo para copiar.',
    body:
      'Analizamos cualquier URL y producimos un dashboard HTML visual cubriendo meta tags, headings, imágenes, enlaces, Open Graph, schema, velocidad, robots.txt y sitemap — con las 5 correcciones principales por impacto y opción de aplicación directa.',
    highlights: [
      'Score global /100 con dashboard HTML visual',
      'Meta tags, headings, imágenes, velocidad, schema y sitemap',
      'Top 5 correcciones con código listo para copiar',
    ],
  },
  {
    slug: 'meta-ads-audit',
    meta: 'Facebook e Instagram',
    title: 'Auditoría Meta Ads',
    tagline:
      'Descubre por qué tus anuncios Meta no convierten — revisión de biblioteca, landing page y coherencia del mensaje.',
    body:
      'Auditamos campañas activas vía la Biblioteca Meta, evaluamos la coherencia de la landing page y reescribimos headlines y CTAs con un checklist priorizado por impacto para saber qué cambiar primero.',
    highlights: [
      'Análisis de anuncios activos vía Biblioteca Meta',
      'Auditoría de landing page y message match (copy / CTA)',
      'Reescrituras de headlines y CTAs con checklist por impacto',
    ],
  },
  {
    slug: 'global-digital-audit',
    meta: 'Diagnóstico 360°',
    title: 'Auditoría Digital Global',
    tagline:
      'Vista 360° de tu negocio online — web, redes, oferta, funnel y coherencia de marca con plan de acción priorizado.',
    body:
      'Ideal como servicio de entrada: puntuamos cada dominio, mapeamos tu funnel actual, detectamos incoherencias y entregamos quick wins semanales más comparación opcional con competidores.',
    highlights: [
      'Score por dominio con incoherencias detectadas',
      'Mapa del funnel actual y plan de acción priorizado',
      'Quick wins semanales + comparación de competidores (opcional)',
    ],
  },
  {
    slug: 'automated-prospecting',
    meta: 'Generación de leads',
    title: 'Prospección Comercial Automatizada',
    tagline:
      'Prospectos calificados por nicho y ciudad, análisis de presencia digital y mensajes de contacto personalizados listos para enviar.',
    body:
      'Construimos un pipeline ordenado por oportunidad en tu sector, analizamos los mejores leads en detalle y generamos mensajes personalizados con exportación a JSON o Google Sheets — para tu equipo o clientes de agencia.',
    highlights: [
      'Pipeline de prospectos ordenado por oportunidad',
      'Análisis detallado de los mejores leads',
      'Mensajes personalizados + exportación JSON / Google Sheets',
    ],
  },
  {
    slug: 'custom-ai-tools',
    meta: 'Premium / presupuesto',
    title: 'Herramientas IA Personalizadas',
    tagline:
      'Herramientas IA a medida para tu industria — automatiza lo que haces manualmente cada semana. CSV a fichas, análisis LinkedIn, cotizaciones automáticas y más.',
    body:
      'Diseñamos e instalamos herramientas IA reutilizables adaptadas a tu workflow — con kits compartibles e instrucciones para que tu equipo las opere de forma independiente. Enganche premium, acotado a tu caso de uso.',
    highlights: [
      'Herramienta reutilizable instalada para tu equipo',
      'Kit compartible con instrucciones de configuración',
      'Automatiza tareas manuales semanales de tu industria',
    ],
  },
];
